import React, { useEffect, useState, useMemo } from 'react';
import { isActiveLikeStatus } from './utils/studentStatus';
import {
  dedupeAttendanceBuckets,
  attendedCountForSession,
  getCampusActiveStudentCount,
  sessionPresentPct
} from './utils/attendanceMath';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssessmentIcon from '@mui/icons-material/Assessment';
import WcIcon from '@mui/icons-material/Wc';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useFirebaseData } from './contexts/FirebaseDataContext';
import { isPresident } from './utils/permissions';
import { buildInstructorByIdMap, buildCourseNameToInstructorMap } from './utils/instructorDisplay';
import { isUrduText, getUrduSxProps } from './utils/urduFont';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

// Enable custom date format parsing (required for DD/MM/YYYY format)
dayjs.extend(customParseFormat);

const Dashboard = () => {
  // Get data from context
  const {
    students: contextStudents,
    courses: contextCourses,
    attendance: contextAttendance,
    campuses: contextCampuses,
    instructors: contextInstructors,
    loading: contextLoading,
    initialLoadComplete
  } = useFirebaseData();

  const navigate = useNavigate();
  const [selectedCampus, setSelectedCampus] = useState('');
  // Only show consolidated dashboard for president, default to campus dashboard for others
  const [activeTab, setActiveTab] = useState(isPresident() ? 0 : 1); // 0 = Consolidated, 1 = Campus Dashboard
  const [openAttendanceBreakdown, setOpenAttendanceBreakdown] = useState(false);
  const carouselRef = React.useRef(null);
  const [carouselPosition, setCarouselPosition] = useState(0);

  // Auto-select campus if only one is available
  useEffect(() => {
    if (initialLoadComplete && contextCampuses.length === 1 && !selectedCampus) {
      setSelectedCampus(contextCampuses[0].campusCode);
    }
  }, [initialLoadComplete, contextCampuses, selectedCampus]);

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Print center-wise breakdown
  const handlePrintBreakdown = () => {
    // Create table HTML for print
    const tableRows = consolidatedStats.map(campus => `
      <tr>
        <td>${campus.campusName || ''}</td>
        <td>${campus.campusCode || ''}</td>
        <td class="text-center">${campus.totalStudents || 0}</td>
        <td class="text-center">${campus.maleStudents || 0} (${campus.malePercentage}%)</td>
        <td class="text-center">${campus.femaleStudents || 0} (${campus.femalePercentage}%)</td>
        <td class="text-center">${campus.activeStudents || 0} (${campus.activePercentage}%)</td>
        <td class="text-center">${campus.inactiveStudents || 0} (${campus.inactivePercentage}%)</td>
        <td class="text-center">${campus.anjumanBefore || 0} (${campus.anjumanBeforePercentage}%)</td>
        <td class="text-center">${campus.anjumanAfter || 0} (${campus.anjumanAfterPercentage}%)</td>
        <td class="text-center">${campus.tanzeemBefore || 0} (${campus.tanzeemBeforePercentage}%)</td>
        <td class="text-center">${campus.tanzeemAfter || 0} (${campus.tanzeemAfterPercentage}%)</td>
      </tr>
    `).join('');

    // Add total row
    const totalRow = `
      <tr style="font-weight: bold; background-color: #e3f2fd;">
        <td>TOTAL</td>
        <td></td>
        <td class="text-center">${totalConsolidatedStats.totalStudents || 0}</td>
        <td class="text-center">${totalConsolidatedStats.maleStudents || 0} (${totalConsolidatedStats.malePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.femaleStudents || 0} (${totalConsolidatedStats.femalePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.activeStudents || 0} (${totalConsolidatedStats.activePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.inactiveStudents || 0} (${totalConsolidatedStats.inactivePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.anjumanBefore || 0} (${totalConsolidatedStats.anjumanBeforePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.anjumanAfter || 0} (${totalConsolidatedStats.anjumanAfterPercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.tanzeemBefore || 0} (${totalConsolidatedStats.tanzeemBeforePercentage}%)</td>
        <td class="text-center">${totalConsolidatedStats.tanzeemAfter || 0} (${totalConsolidatedStats.tanzeemAfterPercentage}%)</td>
      </tr>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Center-wise Student Breakdown</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              color: #2E7D32;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: 600;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .text-center {
              text-align: center;
            }
            @media print {
              body { margin: 0; padding: 10px; }
              @page { margin: 1cm; size: landscape; }
              table { font-size: 10px; }
            }
          </style>
        </head>
        <body>
          <h1>Center-wise Student Breakdown</h1>
          <table>
            <thead>
              <tr>
                <th>Center Name</th>
                <th>Center Code</th>
                <th class="text-center">Total Students</th>
                <th class="text-center">Male</th>
                <th class="text-center">Female</th>
                <th class="text-center">Active</th>
                <th class="text-center">Inactive</th>
                <th class="text-center">Anjuman Before</th>
                <th class="text-center">Anjuman After</th>
                <th class="text-center">Tanzeem Before</th>
                <th class="text-center">Tanzeem After</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              ${totalRow}
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 12px; color: #666;">
            Generated on: ${new Date().toLocaleString()}
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Download center-wise breakdown as CSV
  const handleDownloadBreakdown = () => {
    const headers = [
      'Center Name',
      'Center Code',
      'Total Students',
      'Male',
      'Male %',
      'Female',
      'Female %',
      'Active',
      'Active %',
      'Inactive',
      'Inactive %',
      'Anjuman Before',
      'Anjuman Before %',
      'Anjuman After',
      'Anjuman After %',
      'Tanzeem Before',
      'Tanzeem Before %',
      'Tanzeem After',
      'Tanzeem After %'
    ];

    const rows = consolidatedStats.map(campus => [
      campus.campusName || '',
      campus.campusCode || '',
      campus.totalStudents || 0,
      campus.maleStudents || 0,
      campus.malePercentage || '0.0',
      campus.femaleStudents || 0,
      campus.femalePercentage || '0.0',
      campus.activeStudents || 0,
      campus.activePercentage || '0.0',
      campus.inactiveStudents || 0,
      campus.inactivePercentage || '0.0',
      campus.anjumanBefore || 0,
      campus.anjumanBeforePercentage || '0.0',
      campus.anjumanAfter || 0,
      campus.anjumanAfterPercentage || '0.0',
      campus.tanzeemBefore || 0,
      campus.tanzeemBeforePercentage || '0.0',
      campus.tanzeemAfter || 0,
      campus.tanzeemAfterPercentage || '0.0'
    ]);

    // Add total row
    rows.push([
      'TOTAL',
      '',
      totalConsolidatedStats.totalStudents || 0,
      totalConsolidatedStats.maleStudents || 0,
      totalConsolidatedStats.malePercentage || '0.0',
      totalConsolidatedStats.femaleStudents || 0,
      totalConsolidatedStats.femalePercentage || '0.0',
      totalConsolidatedStats.activeStudents || 0,
      totalConsolidatedStats.activePercentage || '0.0',
      totalConsolidatedStats.inactiveStudents || 0,
      totalConsolidatedStats.inactivePercentage || '0.0',
      totalConsolidatedStats.anjumanBefore || 0,
      totalConsolidatedStats.anjumanBeforePercentage || '0.0',
      totalConsolidatedStats.anjumanAfter || 0,
      totalConsolidatedStats.anjumanAfterPercentage || '0.0',
      totalConsolidatedStats.tanzeemBefore || 0,
      totalConsolidatedStats.tanzeemBeforePercentage || '0.0',
      totalConsolidatedStats.tanzeemAfter || 0,
      totalConsolidatedStats.tanzeemAfterPercentage || '0.0'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Add UTF-8 BOM for proper Urdu/Arabic text display in Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `center-wise-breakdown-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate consolidated stats (all campuses)
  const consolidatedStats = useMemo(() => {
    if (!initialLoadComplete) {
      return [];
    }

    return contextCampuses.map(campus => {
      const campusStudents = contextStudents.filter(s => s.campusCode === campus.campusCode);
      const totalStudents = campusStudents.length;
      const maleStudents = campusStudents.filter(s => {
        const g = (s.gender || '').toString().toLowerCase();
        return g === 'male' || g === 'm';
      }).length;
      const femaleStudents = campusStudents.filter(s => {
        const g = (s.gender || '').toString().toLowerCase();
        return g === 'female' || g === 'f';
      }).length;
      const activeStudents = campusStudents.filter(s => isActiveLikeStatus(s.status)).length;
      const inactiveStudents = campusStudents.filter(s => (s.status || 'Active') === 'InActive').length;

      const malePercentage = totalStudents > 0 ? ((maleStudents / totalStudents) * 100).toFixed(1) : '0.0';
      const femalePercentage = totalStudents > 0 ? ((femaleStudents / totalStudents) * 100).toFixed(1) : '0.0';
      const activePercentage = totalStudents > 0 ? ((activeStudents / totalStudents) * 100).toFixed(1) : '0.0';
      const inactivePercentage = totalStudents > 0 ? ((inactiveStudents / totalStudents) * 100).toFixed(1) : '0.0';

      // Anjuman counts
      const anjumanBefore = campusStudents.filter(s => s.isAnjumanMemberAtJoining === 'Yes').length;
      const anjumanAfter = campusStudents.filter(s => s.isAnjumanMemberAfterJoining === 'Yes').length;
      const anjumanBeforePercentage = totalStudents > 0 ? ((anjumanBefore / totalStudents) * 100).toFixed(1) : '0.0';
      const anjumanAfterPercentage = totalStudents > 0 ? ((anjumanAfter / totalStudents) * 100).toFixed(1) : '0.0';

      // Tanzeem counts
      const tanzeemBefore = campusStudents.filter(s => s.isTanzeemMemberAtJoining === 'Yes').length;
      const tanzeemAfter = campusStudents.filter(s => s.isTanzeemMemberAfterJoining === 'Yes').length;
      const tanzeemBeforePercentage = totalStudents > 0 ? ((tanzeemBefore / totalStudents) * 100).toFixed(1) : '0.0';
      const tanzeemAfterPercentage = totalStudents > 0 ? ((tanzeemAfter / totalStudents) * 100).toFixed(1) : '0.0';

      return {
        campusCode: campus.campusCode,
        campusName: campus.campusName,
        totalStudents,
        maleStudents,
        femaleStudents,
        malePercentage,
        femalePercentage,
        activeStudents,
        inactiveStudents,
        activePercentage,
        inactivePercentage,
        anjumanBefore,
        anjumanAfter,
        anjumanBeforePercentage,
        anjumanAfterPercentage,
        tanzeemBefore,
        tanzeemAfter,
        tanzeemBeforePercentage,
        tanzeemAfterPercentage,
      };
    });
  }, [contextStudents, contextCampuses, initialLoadComplete]);

  const totalConsolidatedStats = useMemo(() => {
    const totals = consolidatedStats.reduce((acc, campus) => ({
      totalStudents: acc.totalStudents + campus.totalStudents,
      maleStudents: acc.maleStudents + campus.maleStudents,
      femaleStudents: acc.femaleStudents + campus.femaleStudents,
      activeStudents: acc.activeStudents + campus.activeStudents,
      inactiveStudents: acc.inactiveStudents + campus.inactiveStudents,
      anjumanBefore: acc.anjumanBefore + campus.anjumanBefore,
      anjumanAfter: acc.anjumanAfter + campus.anjumanAfter,
      tanzeemBefore: acc.tanzeemBefore + campus.tanzeemBefore,
      tanzeemAfter: acc.tanzeemAfter + campus.tanzeemAfter,
    }), {
      totalStudents: 0,
      maleStudents: 0,
      femaleStudents: 0,
      activeStudents: 0,
      inactiveStudents: 0,
      anjumanBefore: 0,
      anjumanAfter: 0,
      tanzeemBefore: 0,
      tanzeemAfter: 0,
    });

    const malePercentage = totals.totalStudents > 0 ? ((totals.maleStudents / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const femalePercentage = totals.totalStudents > 0 ? ((totals.femaleStudents / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const activePercentage = totals.totalStudents > 0 ? ((totals.activeStudents / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const inactivePercentage = totals.totalStudents > 0 ? ((totals.inactiveStudents / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const anjumanBeforePercentage = totals.totalStudents > 0 ? ((totals.anjumanBefore / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const anjumanAfterPercentage = totals.totalStudents > 0 ? ((totals.anjumanAfter / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const tanzeemBeforePercentage = totals.totalStudents > 0 ? ((totals.tanzeemBefore / totals.totalStudents) * 100).toFixed(1) : '0.0';
    const tanzeemAfterPercentage = totals.totalStudents > 0 ? ((totals.tanzeemAfter / totals.totalStudents) * 100).toFixed(1) : '0.0';

    return {
      ...totals,
      malePercentage,
      femalePercentage,
      activePercentage,
      inactivePercentage,
      anjumanBeforePercentage,
      anjumanAfterPercentage,
      tanzeemBeforePercentage,
      tanzeemAfterPercentage,
    };
  }, [consolidatedStats]);

  // Calculate monthly attendance statistics for president dashboard
  // Uses: last session recorded date, then 1 month back (not calendar current month)
  const monthlyAttendanceStats = useMemo(() => {
    if (!initialLoadComplete) {
      return {
        averageAttendance: 0,
        centerWiseAttendance: [],
        highestCenter: null,
        lowestCenter: null,
        highestCourse: null,
        lowestCourse: null
      };
    }

    // Build campus code -> name map (handle both campusName and name fields)
    const getCampusName = (code) => {
      const campus = contextCampuses.find(c => c.campusCode === code);
      return campus ? (campus.campusName || campus.name || code) : code;
    };

    // Parse attendance dates once
    const attendanceWithDates = contextAttendance.map(att => {
      if (!att.startTime) return null;
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      return start.isValid() ? { ...att, _parsedStart: start } : null;
    }).filter(Boolean);

    // Find last session date per center (each center uses its own last session for its 1-month window)
    const lastSessionByCenter = {};
    attendanceWithDates.forEach(att => {
      const campusCode = att.campusCode || 'N/A';
      const start = att._parsedStart;
      if (!lastSessionByCenter[campusCode] || start.isAfter(lastSessionByCenter[campusCode])) {
        lastSessionByCenter[campusCode] = start;
      }
    });

    // Global last session for overall/course stats (fallback to today if none)
    const globalLastSession = attendanceWithDates.length > 0
      ? attendanceWithDates.reduce((latest, att) => 
          att._parsedStart.isAfter(latest) ? att._parsedStart : latest
        , attendanceWithDates[0]._parsedStart)
      : dayjs();
    const globalEndOfRange = globalLastSession.endOf('day');
    const globalStartOfRange = globalEndOfRange.subtract(1, 'month').startOf('day');

    // Center-wise: use EACH center's own last session date, then 1 month back for that center
    const centerTotals = {};
    const courseTotals = {};
    let totalPresent = 0;
    let totalStudents = 0;

    attendanceWithDates.forEach(att => {
      const campusCode = att.campusCode || 'N/A';
      const campusDen = getCampusActiveStudentCount(contextStudents, campusCode);
      const attended = attendedCountForSession(att);

      if (campusDen <= 0) return;

      const courseName = att.courseName || 'N/A';
      const start = att._parsedStart;

      // For center-wise: include if within THIS center's last-session 1-month window
      const centerEnd = lastSessionByCenter[campusCode]?.endOf('day') || globalEndOfRange;
      const centerStart = centerEnd.subtract(1, 'month').startOf('day');
      const inCenterWindow = (start.isAfter(centerStart) || start.isSame(centerStart)) && 
        (start.isBefore(centerEnd) || start.isSame(centerEnd));

      // For overall/course: use global window
      const inGlobalWindow = (start.isAfter(globalStartOfRange) || start.isSame(globalStartOfRange)) && 
        (start.isBefore(globalEndOfRange) || start.isSame(globalEndOfRange));

      if (inCenterWindow) {
        if (!centerTotals[campusCode]) {
          centerTotals[campusCode] = { present: 0, total: 0, sessionPercentages: [] };
        }
        centerTotals[campusCode].present += attended;
        centerTotals[campusCode].total += campusDen;
        const pct = sessionPresentPct(attended, campusDen) ?? 0;
        centerTotals[campusCode].sessionPercentages.push(pct);
      }

      if (inGlobalWindow) {
        totalPresent += attended;
        totalStudents += campusDen;
        if (!courseTotals[courseName]) {
          courseTotals[courseName] = { present: 0, total: 0, centers: new Set() };
        }
        courseTotals[courseName].present += attended;
        courseTotals[courseName].total += campusDen;
        courseTotals[courseName].centers.add(campusCode);
      }
    });

    // Calculate average attendance across all centers
    const averageAttendance = totalStudents > 0 
      ? ((totalPresent / totalStudents) * 100).toFixed(1) 
      : '0.0';

    // Calculate center-wise average attendance + max, min, totalSessions
    const centerWiseAttendance = Object.entries(centerTotals).map(([campusCode, totals]) => {
      const avg = totals.total > 0
        ? ((totals.present / totals.total) * 100).toFixed(1)
        : '0.0';
      const pcts = totals.sessionPercentages || [];
      const maxAttendance = pcts.length > 0 ? Math.max(...pcts).toFixed(1) : '0.0';
      const minAttendance = pcts.length > 0 ? Math.min(...pcts).toFixed(1) : '0.0';
      const totalSessions = pcts.length;
      
      return {
        campusCode: campusCode,
        campusName: getCampusName(campusCode),
        averageAttendance: parseFloat(avg),
        maxAttendance,
        minAttendance,
        totalSessions
      };
    }).sort((a, b) => b.averageAttendance - a.averageAttendance);

    // Find highest and lowest centers
    const highestCenter = centerWiseAttendance.length > 0 ? centerWiseAttendance[0] : null;
    const lowestCenter = centerWiseAttendance.length > 0 ? centerWiseAttendance[centerWiseAttendance.length - 1] : null;

    // Calculate course-wise average attendance and find highest
    const courseWiseAttendance = Object.entries(courseTotals).map(([courseName, totals]) => {
      const avg = totals.total > 0
        ? ((totals.present / totals.total) * 100).toFixed(1)
        : '0.0';
      
      // Get center names from center codes
      const centerNames = Array.from(totals.centers).map(code => getCampusName(code));
      
      return {
        courseName: courseName,
        averageAttendance: parseFloat(avg),
        centers: Array.from(totals.centers),
        centerNames: centerNames
      };
    }).sort((a, b) => b.averageAttendance - a.averageAttendance);

    // Find highest and lowest courses
    const highestCourse = courseWiseAttendance.length > 0 ? courseWiseAttendance[0] : null;
    const lowestCourse = courseWiseAttendance.length > 0 ? courseWiseAttendance[courseWiseAttendance.length - 1] : null;

    return {
      averageAttendance: parseFloat(averageAttendance),
      centerWiseAttendance,
      highestCenter,
      lowestCenter,
      highestCourse,
      lowestCourse
    };
  }, [contextAttendance, contextCampuses, contextStudents, initialLoadComplete]);

  // Calculate recent Special Lectures and Fikr Islami (campus-wise) - using same logic as recent topics
  const lastWeekSpecialLectures = useMemo(() => {
    if (!initialLoadComplete) {
      return { 
        specialLectures: { campusWise: {} }, 
        fikrIslami: { campusWise: {} } 
      };
    }

    // Use same logic as recentRows: today + last 5 unique days with attendance
    const allAttendanceRows = contextAttendance.map(d => {
      // Format is: YYYY-MM-DD-HH:mm (e.g., 2025-05-06-12:00)
      // Convert to standard format by replacing last dash before time with space
      const start = dayjs(d.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      return {
        ...d,
        test: start,
      };
    });

    // Today + last 5 days (unique days with attendance)
    const today = dayjs();
    const allDaysDesc = Array.from(
      new Set(
        allAttendanceRows
          .filter(r => r.test.isValid())
          .map(r => r.test.startOf('day').valueOf())
          .sort((a, b) => b - a)
      )
    );

    const selectedDayTimestamps = allDaysDesc.filter(ts =>
      dayjs(ts).isSame(today, 'day')
    );

    const remainingDayTimestamps = allDaysDesc.filter(ts =>
      !dayjs(ts).isSame(today, 'day')
    );

    const lastFiveDayTimestamps = remainingDayTimestamps.slice(0, 5);
    const allowedDays = new Set([...selectedDayTimestamps, ...lastFiveDayTimestamps]);

    // Filter attendance for recent days (today + last 5 unique days)
    const lastWeekAttendance = allAttendanceRows.filter(attendance => {
      if (!attendance.test || !attendance.test.isValid()) return false;
      return allowedDays.has(attendance.test.startOf('day').valueOf());
    });

    // Filter for Special Lectures
    const specialLectures = lastWeekAttendance.filter(a => 
      a.courseName && (
        a.courseName.toLowerCase().includes('special lecture') ||
        a.courseName.toLowerCase() === 'special lectures'
      )
    );

    // Filter for Fikr Islami
    const fikrIslami = lastWeekAttendance.filter(a => 
      a.courseName && (
        a.courseName.toLowerCase().includes('fikr islami') ||
        (a.courseName.toLowerCase().includes('fikr') && a.courseName.toLowerCase().includes('islami'))
      )
    );

    // Group Special Lectures by campus
    const specialLecturesByCampus = {};
    specialLectures.forEach(att => {
      const campusCode = att.campusCode || 'N/A';
      if (!specialLecturesByCampus[campusCode]) {
        specialLecturesByCampus[campusCode] = [];
      }
      // Use same date parsing logic as recentRows
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const attended = attendedCountForSession(att);
      const campusDen = getCampusActiveStudentCount(contextStudents, campusCode);
      const pct = sessionPresentPct(attended, campusDen);
      const presentPercentage = pct === null ? 0 : pct.toFixed(1);
      specialLecturesByCampus[campusCode].push({
        date: start.isValid() ? start.format('DD/MM/YYYY') : 'N/A',
        time: start.isValid() ? start.format('hh:mm A') : 'N/A',
        presentPercentage,
        topic: att.topic || '-'
      });
    });

    // Group Fikr Islami by campus
    const fikrIslamiByCampus = {};
    fikrIslami.forEach(att => {
      const campusCode = att.campusCode || 'N/A';
      if (!fikrIslamiByCampus[campusCode]) {
        fikrIslamiByCampus[campusCode] = [];
      }
      // Use same date parsing logic as recentRows
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const attended = attendedCountForSession(att);
      const campusDen = getCampusActiveStudentCount(contextStudents, campusCode);
      const pct = sessionPresentPct(attended, campusDen);
      const presentPercentage = pct === null ? 0 : pct.toFixed(1);
      fikrIslamiByCampus[campusCode].push({
        date: start.isValid() ? start.format('DD/MM/YYYY') : 'N/A',
        time: start.isValid() ? start.format('hh:mm A') : 'N/A',
        presentPercentage,
        topic: att.topic || '-'
      });
    });

    // Sort sessions within each campus by date (newest first)
    Object.keys(specialLecturesByCampus).forEach(campusCode => {
      specialLecturesByCampus[campusCode].sort((a, b) => {
        const dateA = dayjs(a.date, 'DD/MM/YYYY');
        const dateB = dayjs(b.date, 'DD/MM/YYYY');
        return dateB - dateA;
      });
    });

    Object.keys(fikrIslamiByCampus).forEach(campusCode => {
      fikrIslamiByCampus[campusCode].sort((a, b) => {
        const dateA = dayjs(a.date, 'DD/MM/YYYY');
        const dateB = dayjs(b.date, 'DD/MM/YYYY');
        return dateB - dateA;
      });
    });

    return {
      specialLectures: {
        campusWise: specialLecturesByCampus
      },
      fikrIslami: {
        campusWise: fikrIslamiByCampus
      }
    };
  }, [contextAttendance, contextStudents, initialLoadComplete]);

  // Calculate campus-wise course attendance for the selected campus
  // Uses same logic as monthlyAttendanceStats: last session date, then 1 month back
  const campusCourseAttendanceStats = useMemo(() => {
    if (!initialLoadComplete || !selectedCampus) {
      return {
        highestCourse: null,
        lowestCourse: null
      };
    }

    // Find last session date from all attendance
    let lastSessionDate = null;
    contextAttendance.forEach(att => {
      if (!att.startTime) return;
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      if (start.isValid() && (!lastSessionDate || start.isAfter(lastSessionDate))) {
        lastSessionDate = start;
      }
    });
    const endOfRange = lastSessionDate ? lastSessionDate.endOf('day') : dayjs().endOf('month');
    const startOfRange = endOfRange.subtract(1, 'month').startOf('day');

    // Filter attendance records for previous month (last session, 1 month back) and selected campus
    const monthlyAttendance = contextAttendance.filter(att => {
      if (!att.startTime || att.campusCode !== selectedCampus) return false;
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      if (!start.isValid()) return false;
      return (start.isAfter(startOfRange) || start.isSame(startOfRange)) && (start.isBefore(endOfRange) || start.isSame(endOfRange));
    });

    // Course-wise totals for selected campus
    const courseTotals = {};
    const courseSessions = {}; // Track individual sessions for date tracking
    const campusDen = getCampusActiveStudentCount(contextStudents, selectedCampus);

    monthlyAttendance.forEach(att => {
      if (campusDen <= 0) return;

      const attended = attendedCountForSession(att);
      const courseName = att.courseName || 'N/A';
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const sessionDate = start.isValid() ? start.format('DD/MM/YYYY') : 'N/A';
      const pct = sessionPresentPct(attended, campusDen);
      const sessionPercentage = pct === null ? '0.0' : pct.toFixed(1);

      if (!courseTotals[courseName]) {
        courseTotals[courseName] = { present: 0, total: 0 };
        courseSessions[courseName] = [];
      }
      courseTotals[courseName].present += attended;
      courseTotals[courseName].total += campusDen;

      // Track individual session for finding best/worst day
      courseSessions[courseName].push({
        date: sessionDate,
        percentage: parseFloat(sessionPercentage)
      });
    });

    // Calculate course-wise average attendance and find best/worst day
    const courseWiseAttendance = Object.entries(courseTotals).map(([courseName, totals]) => {
      const avg = totals.total > 0
        ? ((totals.present / totals.total) * 100).toFixed(1)
        : '0.0';
      
      // Find best and worst day for this course
      const sessions = courseSessions[courseName] || [];
      const bestSession = sessions.length > 0 ? sessions.reduce((best, curr) => 
        curr.percentage > best.percentage ? curr : best
      ) : null;
      const worstSession = sessions.length > 0 ? sessions.reduce((worst, curr) => 
        curr.percentage < worst.percentage ? curr : worst
      ) : null;
      
      return {
        courseName: courseName,
        averageAttendance: parseFloat(avg),
        bestDay: bestSession ? bestSession.date : null,
        bestDayPercentage: bestSession ? bestSession.percentage : null,
        worstDay: worstSession ? worstSession.date : null,
        worstDayPercentage: worstSession ? worstSession.percentage : null
      };
    }).sort((a, b) => b.averageAttendance - a.averageAttendance);

    // Find highest and lowest courses
    const highestCourse = courseWiseAttendance.length > 0 ? courseWiseAttendance[0] : null;
    const lowestCourse = courseWiseAttendance.length > 0 ? courseWiseAttendance[courseWiseAttendance.length - 1] : null;

    return {
      highestCourse,
      lowestCourse
    };
  }, [contextAttendance, contextStudents, selectedCampus, initialLoadComplete]);

  // Calculate stats from context data
  const stats = useMemo(() => {
    if (!initialLoadComplete) {
      return {
        totalStudents: 0,
        maleStudents: 0,
        femaleStudents: 0,
        activeStudents: 0,
        inactiveStudents: 0,
        totalClasses: 0,
        totalPlannedClasses: 0,
        totalRemainingClasses: 0,
      };
    }

    // Filter students by selected campus - show nothing if no campus selected
    const students = selectedCampus
      ? contextStudents.filter(s => s.campusCode === selectedCampus)
      : [];

    const totalStudents = students.length;
    const maleStudents = students.filter(s => {
      const g = (s.gender || '').toString().toLowerCase();
      return g === 'male' || g === 'm';
    }).length;
    const femaleStudents = students.filter(s => {
      const g = (s.gender || '').toString().toLowerCase();
      return g === 'female' || g === 'f';
    }).length;
    const activeStudents = students.filter(s => isActiveLikeStatus(s.status)).length;
    const inactiveStudents = students.filter(s => (s.status || 'Active') === 'InActive').length;

    // Calculate total planned classes from courses (filtered by selected campus) - show nothing if no campus selected
    let totalPlannedClasses = 0;
    const filteredCourses = selectedCampus
      ? contextCourses.filter(course => 
          course.campusCodes && course.campusCodes.includes(selectedCampus)
        )
      : [];
    filteredCourses.forEach(course => {
      totalPlannedClasses += course.totalClasses || 0;
    });

    // Filter attendance by selected campus
    const attendanceData = selectedCampus
      ? contextAttendance.filter(a => a.campusCode === selectedCampus)
      : contextAttendance;

    const totalClasses = attendanceData.length;
    const totalRemainingClasses = totalPlannedClasses - totalClasses;

    const malePercentage = totalStudents > 0 ? ((maleStudents / totalStudents) * 100).toFixed(1) : '0.0';
    const femalePercentage = totalStudents > 0 ? ((femaleStudents / totalStudents) * 100).toFixed(1) : '0.0';
    const activePercentage = totalStudents > 0 ? ((activeStudents / totalStudents) * 100).toFixed(1) : '0.0';
    const inactivePercentage = totalStudents > 0 ? ((inactiveStudents / totalStudents) * 100).toFixed(1) : '0.0';

    return {
      totalStudents,
      maleStudents,
      femaleStudents,
      malePercentage,
      femalePercentage,
      activeStudents,
      inactiveStudents,
      activePercentage,
      inactivePercentage,
      totalClasses,
      totalPlannedClasses,
      totalRemainingClasses,
    };
  }, [contextStudents, contextCourses, contextAttendance, selectedCampus, initialLoadComplete]);

  const instructorById = useMemo(
    () => buildInstructorByIdMap(contextInstructors),
    [contextInstructors]
  );

  // Build course map for instructor lookup (resolved via Instructors directory when linked)
  // Fallback to any matching course name, but prefer selected-campus mapping when available.
  const courseMap = useMemo(
    () => buildCourseNameToInstructorMap(contextCourses, instructorById, selectedCampus),
    [contextCourses, instructorById, selectedCampus]
  );

  // Build attendance rows from context data
  const recentRows = useMemo(() => {
    if (!initialLoadComplete || !selectedCampus) return [];

    // Filter attendance by selected campus
    const attendanceData = contextAttendance.filter(a => a.campusCode === selectedCampus);

    const allRows = attendanceData.map(d => {
      // Format is: YYYY-MM-DD-HH:mm (e.g., 2025-05-06-12:00)
      // Convert to standard format by replacing last dash before time with space
      const start = dayjs(d.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const end = dayjs(d.endTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      return {
        id: d.id,
        date: start.isValid() ? start.format('DD/MM/YYYY') : 'N/A',
        courseName: d.courseName,
        instructor: courseMap[d.courseName] || 'Unknown',
        startTime: start.isValid() ? start.format('hh:mm A') : 'N/A',
        endTime: end.isValid() ? end.format('hh:mm A') : 'N/A',
        topic: d.topic,
        comments: d.comments,
        test: start,
      };
    });

    // Today + last 5 days (unique days with attendance)
    const today = dayjs();
    const allDaysDesc = Array.from(
      new Set(
        allRows
          .filter(r => r.test.isValid())
          .map(r => r.test.startOf('day').valueOf())
          .sort((a, b) => b - a)
      )
    );

    const selectedDayTimestamps = allDaysDesc.filter(ts =>
      dayjs(ts).isSame(today, 'day')
    );

    const remainingDayTimestamps = allDaysDesc.filter(ts =>
      !dayjs(ts).isSame(today, 'day')
    );

    const lastFiveDayTimestamps = remainingDayTimestamps.slice(0, 5);
    const allowedDays = new Set([...selectedDayTimestamps, ...lastFiveDayTimestamps]);

    return allRows
      .filter(row => row.test.isValid() && allowedDays.has(row.test.startOf('day').valueOf()))
      .sort((a, b) => a.test - b.test);
  }, [contextAttendance, selectedCampus, courseMap, initialLoadComplete]);

  // Check if today is an off day (Saturday or Sunday)
  const today = dayjs();
  const todayDayName = today.format('dddd').toLowerCase();
  const isOffDay = todayDayName === 'saturday' || todayDayName === 'sunday';
  
  const todayRows = isOffDay ? [] : recentRows.filter(row => row.test.isSame(today, 'day'));

  const groupedClasses = (() => {
    const grouped = {};
    recentRows.forEach(row => {
      const date = row.date;
      const dayName = row.test.format('dddd').toLowerCase();
      // Skip if it's today and today is an off day (Saturday or Sunday)
      const isTodayOffDay = row.test.isSame(today, 'day') && (dayName === 'saturday' || dayName === 'sunday');
      if (isTodayOffDay) return; // Skip today if it's an off day
      
      if (!grouped[date]) {
        grouped[date] = {
          date,
          dayOfWeek: row.test.format('dddd'),
          classes: [],
        };
      }
      grouped[date].classes.push(row);
    });
    return Object.values(grouped).sort((a, b) => {
      const dateA = dayjs(a.date, 'DD/MM/YYYY');
      const dateB = dayjs(b.date, 'DD/MM/YYYY');
      return dateA - dateB;
    });
  })();

  // Calculate students who haven't attended recent classes (gap > 2 days from most recent attendance)
  const frequentlyAbsentStudents = useMemo(() => {
    if (!initialLoadComplete || !selectedCampus) return [];

    // Get active students for this campus
    const campusStudents = contextStudents.filter(s => s.campusCode === selectedCampus && isActiveLikeStatus(s.status));
    const studentMap = {};
    campusStudents.forEach(s => {
      studentMap[s.id] = { id: s.id, name: s.name };
    });

    // Get ALL attendance data for this campus
    const allAttendanceData = contextAttendance
      .filter(a => a.campusCode === selectedCampus)
      .map(a => {
        const start = dayjs(a.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
        return { ...a, parsedDate: start };
      })
      .filter(a => a.parsedDate.isValid());

    if (allAttendanceData.length === 0) return [];

    // Find the most recent attendance date for this campus
    const mostRecentAttendanceDate = allAttendanceData
      .map(a => a.parsedDate)
      .sort((a, b) => b - a)[0]
      .startOf('day');

    // Track each student's last present date
    const studentLastPresentDate = {}; // studentId -> dayjs date
    
    allAttendanceData.forEach(att => {
      const attDate = att.parsedDate.startOf('day');
      const { presentSet, leaveSet } = dedupeAttendanceBuckets(att);
      const bump = (studentId) => {
        if (studentMap[studentId]) {
          if (!studentLastPresentDate[studentId] || attDate.isAfter(studentLastPresentDate[studentId])) {
            studentLastPresentDate[studentId] = attDate;
          }
        }
      };
      presentSet.forEach(bump);
      leaveSet.forEach(bump);
    });

    // Find students with gap > 2 days from most recent attendance
    const result = [];
    
    Object.keys(studentMap).forEach(studentId => {
      const lastPresentDate = studentLastPresentDate[studentId];
      const student = studentMap[studentId];
      
      let gapDays;
      let lastPresentFormatted;
      
      if (!lastPresentDate) {
        // Student has never been present - include them
        gapDays = 999; // Large number to sort them first
        lastPresentFormatted = 'Never';
      } else {
        gapDays = mostRecentAttendanceDate.diff(lastPresentDate, 'day');
        lastPresentFormatted = lastPresentDate.format('DD/MM/YYYY');
      }
      
      // Only include if gap > 2 days
      if (gapDays > 2) {
        result.push({
          ...student,
          gapDays: gapDays === 999 ? '-' : gapDays,
          lastPresentDate: lastPresentFormatted
        });
      }
    });

    // Sort by gap days (lowest first - most recently absent at top)
    return result.sort((a, b) => {
      if (a.gapDays === '-' && b.gapDays === '-') return 0;
      if (a.gapDays === '-') return 1;  // Never present goes to bottom
      if (b.gapDays === '-') return -1; // Never present goes to bottom
      return a.gapDays - b.gapDays;     // Lowest gap first
    });
  }, [contextAttendance, contextStudents, selectedCampus, initialLoadComplete]);

  const statusChartData = [
    { name: 'Active', value: stats.activeStudents },
    { name: 'Inactive', value: stats.inactiveStudents },
  ].filter(item => item.value > 0);

  const genderChartData = [
    { name: 'Male', value: stats.maleStudents },
    { name: 'Female', value: stats.femaleStudents },
  ].filter(item => item.value > 0);

  const recentTopicCounts = recentRows.reduce((acc, row) => {
    const topic = (row.topic || '').trim();
    if (!topic) return acc;
    acc[topic] = (acc[topic] || 0) + 1;
    return acc;
  }, {});

  const recentTopicData = Object.entries(recentTopicCounts)
    .map(([topic, count]) => ({ topic, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const STATUS_COLORS = ['#4CAF50', '#FF9800'];

  const recentDayCount = new Set(recentRows.map(r => r.date)).size;

  if (contextLoading || !initialLoadComplete) {
    return (
      <Box mt={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box mt={4}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#2E7D32' }}>
        📊 Dashboard
      </Typography>

      {/* Tabs for Consolidated and Campus Dashboard - Only show tabs if president */}
      {isPresident() && (
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              },
            }}
          >
            <Tab label="Consolidated Dashboard" />
            <Tab label="Campus Dashboard" />
          </Tabs>
        </Paper>
      )}

      {/* Consolidated Dashboard */}
      {activeTab === 0 && (
        <Box>
          {/* Total Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderLeft: '5px solid #4CAF50',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Students (All Centers)
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {totalConsolidatedStats.totalStudents}
                      </Typography>
                    </Box>
                    <WcIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Male: ${totalConsolidatedStats.maleStudents} (${totalConsolidatedStats.malePercentage}%)`}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(33,150,243,0.15)', 
                        color: '#1565C0',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontWeight: 700
                        }
                      }}
                    />
                    <Chip
                      label={`Female: ${totalConsolidatedStats.femaleStudents} (${totalConsolidatedStats.femalePercentage}%)`}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(233,30,99,0.15)', 
                        color: '#AD1457',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontWeight: 700
                        }
                      }}
                    />
                    <Chip
                      label={`Active: ${totalConsolidatedStats.activeStudents} (${totalConsolidatedStats.activePercentage}%)`}
                      size="small"
                      color="success"
                      sx={{
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontWeight: 700
                        }
                      }}
                    />
                    <Chip
                      label={`Inactive: ${totalConsolidatedStats.inactiveStudents} (${totalConsolidatedStats.inactivePercentage}%)`}
                      size="small"
                      color="warning"
                      sx={{
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontWeight: 700
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderLeft: '5px solid #1976D2',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Total Centers
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {contextCampuses.length}
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 40, color: '#1976D2' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card
                onClick={() => setOpenAttendanceBreakdown(true)}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderLeft: '5px solid #FF9800',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Average Attendance (Previous Month)
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {monthlyAttendanceStats.averageAttendance}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Across all centers (Click for details)
                      </Typography>
                    </Box>
                    <EventAvailableIcon sx={{ fontSize: 40, color: '#FF9800' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Highest and Lowest Attendance Cards - Auto-scrolling Carousel */}
          <Box 
            ref={carouselRef}
            sx={{ 
              mb: 3, 
              overflowX: 'hidden', 
              overflowY: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 3, 
                pb: 2,
                width: 'max-content',
                '@keyframes scroll': {
                  '0%': { transform: 'translateX(0)' },
                  '100%': { transform: 'translateX(calc(-50% - 18px))' }
                },
                animation: 'scroll 60s linear infinite',
              }}
            >
              {/* Highest Center */}
              {monthlyAttendanceStats.highestCenter && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #4CAF50',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Highest Attendance Center (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.highestCenter.campusName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#4CAF50', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.highestCenter.averageAttendance}%
                          </Typography>
                        </Box>
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 36, color: '#4CAF50', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {/* Lowest Center */}
              {monthlyAttendanceStats.lowestCenter && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #FF5722',
                    background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(255, 87, 34, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Lowest Attendance Center (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#D84315', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.lowestCenter.campusName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF5722', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.lowestCenter.averageAttendance}%
                          </Typography>
                        </Box>
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 36, color: '#FF5722', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {/* Highest Course */}
              {monthlyAttendanceStats.highestCourse && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #9C27B0',
                    background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Highest Attendance Course (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#7B1FA2', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.highestCourse.courseName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#9C27B0', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.highestCourse.averageAttendance}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {monthlyAttendanceStats.highestCourse.centerNames && monthlyAttendanceStats.highestCourse.centerNames.map((centerName, idx) => (
                            <Chip
                              key={idx}
                              label={centerName}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: '#9C27B0', color: '#9C27B0' }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <AssessmentIcon sx={{ fontSize: 36, color: '#9C27B0', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {/* Lowest Course */}
              {monthlyAttendanceStats.lowestCourse && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #E91E63',
                    background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(233, 30, 99, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Lowest Attendance Course (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#C2185B', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.lowestCourse.courseName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#E91E63', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.lowestCourse.averageAttendance}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {monthlyAttendanceStats.lowestCourse.centerNames && monthlyAttendanceStats.lowestCourse.centerNames.map((centerName, idx) => (
                            <Chip
                              key={idx}
                              label={centerName}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: '#E91E63', color: '#E91E63' }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <AssessmentIcon sx={{ fontSize: 36, color: '#E91E63', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {/* Duplicate cards for seamless loop */}
              {monthlyAttendanceStats.highestCenter && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #4CAF50',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Highest Attendance Center (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.highestCenter.campusName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#4CAF50', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.highestCenter.averageAttendance}%
                          </Typography>
                        </Box>
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 36, color: '#4CAF50', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {monthlyAttendanceStats.lowestCenter && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #FF5722',
                    background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(255, 87, 34, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Lowest Attendance Center (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#D84315', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.lowestCenter.campusName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF5722', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.lowestCenter.averageAttendance}%
                          </Typography>
                        </Box>
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 36, color: '#FF5722', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {monthlyAttendanceStats.highestCourse && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #9C27B0',
                    background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Highest Attendance Course (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#7B1FA2', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.highestCourse.courseName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#9C27B0', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.highestCourse.averageAttendance}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {monthlyAttendanceStats.highestCourse.centerNames && monthlyAttendanceStats.highestCourse.centerNames.map((centerName, idx) => (
                            <Chip
                              key={idx}
                              label={centerName}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: '#9C27B0', color: '#9C27B0' }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <AssessmentIcon sx={{ fontSize: 36, color: '#9C27B0', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {monthlyAttendanceStats.lowestCourse && (
                <Card
                  sx={{
                    width: 350,
                    height: 180,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderLeft: '5px solid #E91E63',
                    background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(233, 30, 99, 0.05) 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                          Lowest Attendance Course (Previous Month)
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#C2185B', mb: 1, fontSize: '1.1rem' }}>
                          {monthlyAttendanceStats.lowestCourse.courseName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#E91E63', fontSize: '0.95rem' }}>
                            Avg: {monthlyAttendanceStats.lowestCourse.averageAttendance}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {monthlyAttendanceStats.lowestCourse.centerNames && monthlyAttendanceStats.lowestCourse.centerNames.map((centerName, idx) => (
                            <Chip
                              key={idx}
                              label={centerName}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.7rem', borderColor: '#E91E63', color: '#E91E63' }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <AssessmentIcon sx={{ fontSize: 36, color: '#E91E63', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          {/* Attendance Breakdown Dialog */}
          <Dialog
            open={openAttendanceBreakdown}
            onClose={() => setOpenAttendanceBreakdown(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                Center-wise Attendance Breakdown (Previous Month)
              </Typography>
              <IconButton
                onClick={() => setOpenAttendanceBreakdown(false)}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Center Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Center Code</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Average Attendance</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Max Attendance</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Min Attendance</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Total Sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyAttendanceStats.centerWiseAttendance.map((center) => {
                      // Use precomputed values from monthlyAttendanceStats (same per-center window)
                      const maxAttendance = center.maxAttendance ?? '0.0';
                      const minAttendance = center.minAttendance ?? '0.0';
                      const totalSessions = center.totalSessions ?? 0;

                      return (
                        <TableRow
                          key={center.campusCode}
                          sx={{
                            '&:hover': { bgcolor: '#f9f9f9' },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {center.campusName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={center.campusCode}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                              {center.averageAttendance}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${maxAttendance}%`}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${minAttendance}%`}
                              size="small"
                              color="error"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {totalSessions}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <Button onClick={() => setOpenAttendanceBreakdown(false)} color="secondary">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Center-wise Breakdown Table */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                  Center-wise Student Breakdown
                </Typography>
                <Box>
                  <Tooltip title="Print">
                    <IconButton
                      onClick={handlePrintBreakdown}
                      sx={{ color: '#2E7D32' }}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download CSV">
                    <IconButton
                      onClick={handleDownloadBreakdown}
                      sx={{ color: '#2E7D32' }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <TableContainer id="center-wise-breakdown-table">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Center Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Center Code</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Total Students</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Male</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Female</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Active</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Inactive</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Anjuman Before</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Anjuman After</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Tanzeem Before</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Tanzeem After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consolidatedStats.map((campus) => (
                      <TableRow
                        key={campus.campusCode}
                        sx={{
                          '&:hover': { bgcolor: '#f9f9f9' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {campus.campusName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campus.campusCode}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                            {campus.totalStudents}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`Male: ${campus.maleStudents} (${campus.malePercentage}%)`}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(33,150,243,0.15)', 
                              color: '#1565C0',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`Female: ${campus.femaleStudents} (${campus.femalePercentage}%)`}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(233,30,99,0.15)', 
                              color: '#AD1457',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`Active: ${campus.activeStudents} (${campus.activePercentage}%)`}
                            size="small"
                            color="success"
                            sx={{
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`Inactive: ${campus.inactiveStudents} (${campus.inactivePercentage}%)`}
                            size="small"
                            color="warning"
                            sx={{
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${campus.anjumanBefore} (${campus.anjumanBeforePercentage}%)`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(156,39,176,0.15)',
                              color: '#7B1FA2',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${campus.anjumanAfter} (${campus.anjumanAfterPercentage}%)`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(156,39,176,0.25)',
                              color: '#6A1B9A',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${campus.tanzeemBefore} (${campus.tanzeemBeforePercentage}%)`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,152,0,0.15)',
                              color: '#E65100',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${campus.tanzeemAfter} (${campus.tanzeemAfterPercentage}%)`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,152,0,0.25)',
                              color: '#BF360C',
                              fontWeight: 700,
                              '& .MuiChip-label': {
                                fontWeight: 700
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          {/* Last Week Special Lectures and Fikr Islami - Campus Wise */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              mb: 3,
              mt: 3,
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#2E7D32' }}>
                Recent Special Lectures & Fikr Islami Sessions
              </Typography>
              
              <Grid container spacing={3}>
                {/* Special Lectures - Campus Wise */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#FF9800' }}>
                    Special Lectures
                  </Typography>
                  {Object.keys(lastWeekSpecialLectures.specialLectures.campusWise).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No Special Lectures in the last week
                    </Typography>
                  ) : (
                    Object.keys(lastWeekSpecialLectures.specialLectures.campusWise).map(campusCode => {
                      const campus = contextCampuses.find(c => c.campusCode === campusCode);
                      const campusName = campus ? campus.campusName : campusCode;
                      const sessions = lastWeekSpecialLectures.specialLectures.campusWise[campusCode];
                      return (
                        <Paper key={campusCode} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#FF9800' }}>
                            {campusName}
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Present %</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sessions.map((session, index) => (
                                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                                    <TableCell>{session.date}</TableCell>
                                    <TableCell>{session.time}</TableCell>
                                    <TableCell align="center">
                                      <Chip 
                                        label={`${session.presentPercentage}%`} 
                                        size="small" 
                                        color="success"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        className={isUrduText(session.topic || '') ? 'urdu' : ''}
                                        sx={{ 
                                          ...getUrduSxProps(session.topic || ''),
                                          direction: 'ltr',
                                          textAlign: 'left',
                                          maxWidth: 200, 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis' 
                                        }} 
                                        title={session.topic}
                                      >
                                        {session.topic}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      );
                    })
                  )}
                </Grid>

                {/* Fikr Islami - Campus Wise */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#9C27B0' }}>
                    Fikr Islami
                  </Typography>
                  {Object.keys(lastWeekSpecialLectures.fikrIslami.campusWise).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No Fikr Islami in the last week
                    </Typography>
                  ) : (
                    Object.keys(lastWeekSpecialLectures.fikrIslami.campusWise).map(campusCode => {
                      const campus = contextCampuses.find(c => c.campusCode === campusCode);
                      const campusName = campus ? campus.campusName : campusCode;
                      const sessions = lastWeekSpecialLectures.fikrIslami.campusWise[campusCode];
                      return (
                        <Paper key={campusCode} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#9C27B0' }}>
                            {campusName}
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>Present %</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sessions.map((session, index) => (
                                  <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                                    <TableCell>{session.date}</TableCell>
                                    <TableCell>{session.time}</TableCell>
                                    <TableCell align="center">
                                      <Chip 
                                        label={`${session.presentPercentage}%`} 
                                        size="small" 
                                        color="success"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        className={isUrduText(session.topic || '') ? 'urdu' : ''}
                                        sx={{ 
                                          ...getUrduSxProps(session.topic || ''),
                                          direction: 'ltr',
                                          textAlign: 'left',
                                          maxWidth: 200, 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis' 
                                        }} 
                                        title={session.topic}
                                      >
                                        {session.topic}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      );
                    })
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Campus Dashboard (Existing View) */}
      {activeTab === 1 && (
        <Box>
          {/* Campus Selection */}
          <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
            <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
              <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
              <Select
                value={selectedCampus}
                onChange={handleCampusChange}
                input={<OutlinedInput label="Select Campus" />}
                disabled={contextCampuses.length === 1}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2E7D32',
                    },
                  },
                }}
              >
                {contextCampuses.map((campus) => (
                  <MenuItem key={campus.id} value={campus.campusCode}>
                    {campus.campusName} ({campus.campusCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

      {!selectedCampus && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            Please select a campus to view dashboard data
          </Typography>
        </Paper>
      )}

      {selectedCampus && (
        <>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card
            onClick={() => navigate('/students')}
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderLeft: '5px solid #4CAF50',
              cursor: 'pointer',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Students Overview
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalStudents}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Students
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#2E7D32' }}>
                    View Student Detail ↗
                  </Typography>
                </Box>
                <WcIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Male: ${stats.maleStudents} (${stats.malePercentage}%)`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/students?campusCode=${encodeURIComponent(selectedCampus)}&gender=male`);
                  }}
                  sx={{ 
                    bgcolor: 'rgba(33,150,243,0.15)', 
                    color: '#1565C0',
                    fontWeight: 700,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontWeight: 700
                    }
                  }}
                />
                <Chip
                  label={`Female: ${stats.femaleStudents} (${stats.femalePercentage}%)`}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/students?campusCode=${encodeURIComponent(selectedCampus)}&gender=female`);
                  }}
                  sx={{ 
                    bgcolor: 'rgba(233,30,99,0.15)', 
                    color: '#AD1457',
                    fontWeight: 700,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontWeight: 700
                    }
                  }}
                />
                <Chip
                  label={`Active: ${stats.activeStudents} (${stats.activePercentage}%)`}
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/students?campusCode=${encodeURIComponent(selectedCampus)}&status=active`);
                  }}
                  sx={{
                    fontWeight: 700,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontWeight: 700
                    }
                  }}
                />
                <Chip
                  label={`Inactive: ${stats.inactiveStudents} (${stats.inactivePercentage}%)`}
                  size="small"
                  color="warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/students?campusCode=${encodeURIComponent(selectedCampus)}&status=inactive`);
                  }}
                  sx={{
                    fontWeight: 700,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      fontWeight: 700
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              borderLeft: '5px solid #1976D2',
            }}
          >
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Classes Overview
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalClasses}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Classes Conducted
                  </Typography>
                </Box>
                <AssessmentIcon sx={{ fontSize: 40, color: '#1976D2' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Total Planned: ${stats.totalPlannedClasses}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(25,118,210,0.06)' }}
                />
                <Chip
                  label={`Remaining: ${stats.totalRemainingClasses}`}
                  size="small"
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Highest and Lowest Attendance Classes for Campus */}
      {(campusCourseAttendanceStats.highestCourse || campusCourseAttendanceStats.lowestCourse) && (
        <Box sx={{ mb: 3, overflowX: 'auto', '&::-webkit-scrollbar': { height: '8px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px', '&:hover': { background: '#555' } } }}>
          <Box sx={{ display: 'flex', gap: 3, pb: 1 }}>
            {/* Highest Course */}
            {campusCourseAttendanceStats.highestCourse && (
              <Card
                sx={{
                  flex: 1,
                  minWidth: 300,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderLeft: '5px solid #9C27B0',
                  background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flex: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography color="textSecondary" gutterBottom>
                        Highest Attendance Class (Previous Month)
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#7B1FA2', mb: 1 }}>
                        {campusCourseAttendanceStats.highestCourse.courseName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                          Avg: {campusCourseAttendanceStats.highestCourse.averageAttendance}%
                        </Typography>
                      </Box>
                      {campusCourseAttendanceStats.highestCourse.bestDay && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Chip
                            label={`Best: ${campusCourseAttendanceStats.highestCourse.bestDay} (${campusCourseAttendanceStats.highestCourse.bestDayPercentage}%)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      )}
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 48, color: '#9C27B0', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                  </Box>
                </CardContent>
              </Card>
            )}
            {/* Lowest Course */}
            {campusCourseAttendanceStats.lowestCourse && (
              <Card
                sx={{
                  flex: 1,
                  minWidth: 300,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderLeft: '5px solid #E91E63',
                  background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(233, 30, 99, 0.05) 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flex: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography color="textSecondary" gutterBottom>
                        Lowest Attendance Class (Previous Month)
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#C2185B', mb: 1 }}>
                        {campusCourseAttendanceStats.lowestCourse.courseName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#E91E63' }}>
                          Avg: {campusCourseAttendanceStats.lowestCourse.averageAttendance}%
                        </Typography>
                      </Box>
                      {campusCourseAttendanceStats.lowestCourse.worstDay && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Chip
                            label={`Worst: ${campusCourseAttendanceStats.lowestCourse.worstDay} (${campusCourseAttendanceStats.lowestCourse.worstDayPercentage}%)`}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      )}
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 48, color: '#E91E63', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      )}

      {/* Today section - Only show if today is not an off day */}
      {!isOffDay && (
        <Box sx={{ mb: 3 }}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Today&apos;s Attendance
              </Typography>
              {todayRows.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Attendance marked for <strong>{todayRows.length}</strong> classes today.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No attendance marked for today yet.
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/attendance')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Go to Attendance
            </Button>
          </Paper>
        </Box>
      )}

      {/* Frequently Absent Students Section */}
      {frequentlyAbsentStudents.length > 0 && (
        <Box mt={2} mb={2}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              borderLeft: '4px solid #f44336',
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, rgba(244, 67, 54, 0.02) 100%)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#c62828', display: 'flex', alignItems: 'center', gap: 1 }}>
                ⚠️ Students Missing from Recent Classes ({frequentlyAbsentStudents.length})
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 250 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 0.5, fontSize: '0.8rem', bgcolor: '#fce4ec' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5, fontSize: '0.8rem', bgcolor: '#fce4ec' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5, fontSize: '0.8rem', bgcolor: '#fce4ec' }}>Last Present</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5, fontSize: '0.8rem', textAlign: 'center', bgcolor: '#fce4ec' }}>Gap (Days)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {frequentlyAbsentStudents.map((student, idx) => (
                    <TableRow 
                      key={student.id}
                      onClick={() => navigate(`/students?studentId=${encodeURIComponent(student.id)}`)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' },
                        bgcolor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'
                      }}
                    >
                      <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>{student.id}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.8rem', fontWeight: 500 }}>{student.name}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>{student.lastPresentDate}</TableCell>
                      <TableCell sx={{ py: 0.5, textAlign: 'center' }}>
                        <Chip 
                          label={student.gapDays === '-' ? 'Never' : student.gapDays}
                          size="small"
                          sx={{ 
                            bgcolor: student.gapDays === '-' || student.gapDays > 7 ? '#f44336' : '#ff9800',
                            color: 'white',
                            fontWeight: 600,
                            height: 20,
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
          Recent Attendance Sessions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Displaying classes from the last {recentDayCount} days with attendance records.
        </Typography>
        {groupedClasses.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No classes found for this week
            </Typography>
          </Paper>
        ) : (
          groupedClasses.map((dayGroup, index) => (
            <Accordion
              key={index}
              defaultExpanded={index === 0}
              sx={{
                mb: 2,
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#eeeeee',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                    📅 {dayGroup.dayOfWeek}
                  </Typography>
                  <Chip 
                    label={dayGroup.date} 
                    color="primary" 
                    size="small"
                  />
                  <Chip 
                    label={`${dayGroup.classes.length} ${dayGroup.classes.length === 1 ? 'Class' : 'Classes'}`}
                    color="success"
                    size="small"
                  />
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent accordion from expanding/collapsing
                        // Pass date in DD/MM/YYYY format, URL encoded to handle slashes
                        navigate(`/attendance?date=${encodeURIComponent(dayGroup.date)}`);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Modify
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Instructor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dayGroup.classes.map((classItem, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            '&:hover': { bgcolor: '#f5f5f5' },
                            '&:last-child td': { border: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {classItem.startTime} - {classItem.endTime}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={classItem.courseName}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{classItem.instructor}</TableCell>
                          <TableCell align="center">
                            <Typography 
                              className={isUrduText(classItem.topic || '') ? 'urdu' : ''} 
                              variant="body2"
                              sx={{
                                ...getUrduSxProps(classItem.topic || ''),
                                direction: 'ltr',
                                textAlign: 'left'
                              }}
                            >
                              {classItem.topic || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              className={isUrduText(classItem.comments || '') ? 'urdu' : ''} 
                              variant="body2"
                              sx={{
                                ...getUrduSxProps(classItem.comments || ''),
                                direction: 'ltr',
                                textAlign: 'left'
                              }}
                            >
                              {classItem.comments || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
        </>
      )}
      </Box>
      )}
    </Box>
  );
};

export default Dashboard;


